import React from 'react';
import { 
  ArrowPathIcon, 
  ChevronRightIcon,
  SparklesIcon,
  LightBulbIcon,
  MegaphoneIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';

const EmployerBrandStage2 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 2: Embed & Activate Your EVP" icon={<ArrowPathIcon className="h-7 w-7 text-primary-light" />} heading>
      {/* Introduction */}
      <PlaybookCard>
        <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg mb-6">
          <p className="text-gray-300 mb-4">
            By now you've most likely done the work you need to do around EVP discovery & internal activation. That means you've:
          </p>
          <ul className="space-y-2 mb-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">Communicated what you're doing & why</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">Surveyed & Interviewed your team</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">Understood two things from that data:</span>
            </li>
            <li className="flex items-start pl-6">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">Top 3-5 EVP pillars</span>
            </li>
            <li className="flex items-start pl-6">
              <span className="text-primary-light mr-2">—</span>
              <span className="text-gray-300">Top 'opportunity' areas to improve PX</span>
            </li>
          </ul>
          
        </div>
      </PlaybookCard>

      {/* Watch This Section */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <SparklesIcon className="h-7 w-7 text-primary-light mr-2" />
          Watch This
        </h3>
        <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
          <iframe
            src="https://www.loom.com/embed/191d1a9f1b9e43f5bf1da5a4e59e3e9a?sid=ce100d82-0b51-48e4-b5d1-ebe0eb7ec1a8"
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          />
        </div>
      </PlaybookCard>

      {/* What You Need Section */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <LightBulbIcon className="h-7 w-7 text-primary-light mr-2" />
          What You Need
        </h3>
        <ul className="space-y-2">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">•</span>
            <span className="text-gray-300">Agreement & Alignment on just how open & transparent you want to be externally</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">•</span>
            <span className="text-gray-300">Appropriate channels / assets to communicate through</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">•</span>
            <span className="text-gray-300">A comms plan to the wider business</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">•</span>
            <span className="text-gray-300">An external lens to provide an objective 'outside' view</span>
          </li>
        </ul>
      </PlaybookCard>

      {/* Embedding EVP Internally */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <MegaphoneIcon className="h-7 w-7 text-primary-light mr-2" />
          Embedding EVP Internally
        </h3>
        
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-semibold text-white mb-2">Communicating What You've Learned</h4>
            <p className="text-gray-300 mb-4">
              By now, you've gathered rich, unfiltered insights from your team about what it's really like to work here—the highlights, the trade-offs, and the tough moments. Now, it's time to close the loop and communicate back in a way that builds trust and moves the culture forward.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-2">Key Comms Goals</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">•</span>
                <span className="text-gray-300">Show your team they were heard</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">•</span>
                <span className="text-gray-300">Clarify what's staying the same vs. what's changing</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">•</span>
                <span className="text-gray-300">Keep it honest, digestible, and action-oriented</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-white mb-2">How to Roll It Out</h4>
            
            <div className="space-y-4">
              <div>
                <h5 className="text-md font-semibold text-white mb-2">1. Internal Summary to All Employees</h5>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">A short, digestible summary of key themes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Reinforce what's great about working here</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Be upfront about trade-offs and tough spots</span>
                  </li>
                </ul>
              </div>

              <div>
                <h5 className="text-md font-semibold text-white mb-2">2. Hiring & Interviewing Teams</h5>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Provide hiring managers with a concise briefing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Refresh job adverts and career site</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Train interviewers to honestly communicate both the best parts and the trade-offs</span>
                  </li>
                </ul>
              </div>

              <div>
                <h5 className="text-md font-semibold text-white mb-2">3. Leadership & People Team Action Plan</h5>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Identify quick wins vs. longer-term shifts</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Assign owners and next steps</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-light mr-2">—</span>
                    <span className="text-gray-300">Keep a lightweight quarterly check-in</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </PlaybookCard>

      {/* Assessing Current Messaging */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <DocumentTextIcon className="h-7 w-7 text-primary-light mr-2" />
          Assessing Current Messaging & Brand
        </h3>
        
        <div className="space-y-6">
          <p className="text-gray-300 mb-4">
            You have two options here:
          </p>

          <div className="space-y-4">
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">1. Internal Review</h4>
              <p className="text-gray-300 mb-2">Leverage a cross-section of your own team internally to thoroughly review & assess your current job adverts, career site, and any other candidate-facing assets.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <h5 className="text-md font-semibold text-white mb-2">Pros</h5>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">+</span>
                      <span className="text-gray-300">No need to source external help</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">+</span>
                      <span className="text-gray-300">Team has knowledge of 'real' EVP</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 className="text-md font-semibold text-white mb-2">Cons</h5>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">-</span>
                      <span className="text-gray-300">Takes up team time</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">-</span>
                      <span className="text-gray-300">Less objective than external review</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-white mb-2">2. External Review</h4>
              <p className="text-gray-300 mb-2">Find someone externally to spend time reviewing your messaging & brand, and gather some unbiased feedback.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <h5 className="text-md font-semibold text-white mb-2">Pros</h5>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">+</span>
                      <span className="text-gray-300">Most unbiased, powerful feedback</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">+</span>
                      <span className="text-gray-300">No time cost to employees</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h5 className="text-md font-semibold text-white mb-2">Cons</h5>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">-</span>
                      <span className="text-gray-300">May have associated costs</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <CopyToNotion title="Review Questions Template">
            <div className="space-y-4">
              <p className="text-gray-300">What does our {'{career site / job advert / handbook}'} suggest to you about our {'{culture / ways of work / flexibility / career development / diversity etc}'} what assumptions might you make based on what you read?</p>
              <p className="text-gray-300">What sort of person do you think does well here?</p>
              <p className="text-gray-300">What sort of person do you think doesn't do well here?</p>
              <p className="text-gray-300">Are you clear on why you should apply?</p>
              <p className="text-gray-300">Are you clear on why you shouldn't apply?</p>
              <p className="text-gray-300">Why might someone else not like it here?</p>
              <p className="text-gray-300">Is there any information you can't find that you would like to see before applying?</p>
            </div>
          </CopyToNotion>
        </div>
      </PlaybookCard>

      {/* Refreshing & Aligning to EVP */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <ArrowPathIcon className="h-7 w-7 text-primary-light mr-2" />
          Refreshing & Aligning to EVP
        </h3>
        
        <div className="space-y-6">
          <p className="text-gray-300 mb-4">
            With the above work done, you should now be super clear & aligned internally on:
          </p>

          <ul className="space-y-2 mb-6">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">The most valuable things about working here</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">The reasons why your company might not be right for someone</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">How open you want to be with applicants about both</span>
            </li>
          </ul>

          <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg">
            <p className="text-gray-300 mb-2 font-semibold">Question: "Surely it's going to sound negative trying to tell people why they shouldn't apply?"</p>
            <p className="text-gray-300">Answer: Not if you do it like these folks do…</p>
          </div>

          <div className="space-y-6">
            <div>
              <h4 className="text-xl font-semibold text-white mb-2">Some of our favourite examples</h4>
             
            </div>

            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/message-from-posthog-co-founder.png"
              alt="Message from PostHog Co-Founder"
              caption="Message from PostHog Co-Founder"
              href="https://www.posthog.com/careers"
            />

            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/why-you-shouldnt-join-plain.png"
              alt="Why You Shouldn't Join Plain"
              caption="Plain.com Careers Site"
              href="https://plain.com/careers"
            />

            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/posthog-warning.png"
              alt="PostHog Warning"
              caption="PostHog Careers Site"
              href="https://www.posthog.com/careers"
            />

            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/you-should-not-apply-posthog.png"
              alt="You Should Not Apply - PostHog"
              caption="PostHog Careers Site"
              href="https://www.posthog.com/careers"
            />
          </div>
        </div>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default EmployerBrandStage2; 